import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, from } from 'rxjs';
import { switchMap, tap, catchError, takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { ProfileService } from '../profile/profile.service';
import { LogRocketService } from '../../shared/services/log-rocket.service';
import { LogRocketUser } from '../../shared/models/log-rocket.model';
import { BotService } from '../../chatstyle/bot/bot.service';
import { OrganizationService } from '../../chatstyle/organization/organization.service';
import { OrgNotificationService } from '../../shared/services/org-notification.service';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss']
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  showProvisioning = false;
  private static readonly PROVISIONING_FLOOR_MS = 3000;

  private navigateAfterFloor(url: string) {
    this.showProvisioning = true;
    setTimeout(() => this.router.navigateByUrl(url), AuthCallbackComponent.PROVISIONING_FLOOR_MS);
  }

  constructor(
    private authService: AuthService,
    private profileService: ProfileService,
    private router: Router,
    private logRocketService: LogRocketService,
    private botService: BotService,
    private orgService: OrganizationService,
    private orgNotificationService: OrgNotificationService
  ) { }

  ngOnInit() {
    // Convert completeAuthentication Promise to Observable and chain profile load
    from(this.authService.completeAuthentication()).pipe(
      tap(user => {
        // Initialize LogRocket after successful authentication
        this.logRocketService.initialize();
      }),
      switchMap(user =>
        this.profileService.load().pipe(
          tap(profile => {

            // Identify user in LogRocket
            if (profile && profile.email) {
              const logRocketUser: LogRocketUser = {
                id: profile.email,
                name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.email,
                email: profile.email,
                companyName: profile.companyName,
                status: profile.status,
                privileges: profile.privileges
              };

              this.logRocketService.identifyUser(logRocketUser);
            }

            // Connect to org SignalR group for real-time notifications
            this.orgService.getMyOrganization().subscribe({
              next: (org) => {
                if (org && org.id) {
                  this.orgNotificationService.connect(org.id);
                }
              },
              error: (err) => console.warn('Could not load org for notifications:', err)
            });

            // Check if user is in onboarding state
            if (this.profileService.isOnboarding()) {
              this.navigateAfterFloor('/setup/setupwizard/step1');
            } else {
              // Check if user is SuperAdmin
              const isSuperAdmin = this.profileService.hasPrivilege('SuperAdmin');

              if (isSuperAdmin) {
                // SuperAdmin: Allow access to everything, navigate normally
                const savedUrl = user?.state || '/bots';
                this.router.navigateByUrl(savedUrl).catch(navError => {
                  this.router.navigateByUrl('/bots');
                });
              } else {
                // Non-SuperAdmin: Check bot state
                this.botService.getBots().subscribe({
                  next: (bots) => {
                    if (!bots || bots.length === 0) {
                      // No bots = no org association (user was removed from org)
                      this.navigateAfterFloor('/setup/setupwizard/step1');
                      return;
                    }

                    // Check if any bot is in 'setup' state
                    const hasSetupBot = bots.some(bot => bot.state === 'setup');
                    const isOnboardingUser = this.profileService.hasPrivilege('Onboarding');

                    if (hasSetupBot && isOnboardingUser) {
                      // Only redirect to onboarding if the user owns the setup
                      // (invited users have 'User' privilege, not 'Onboarding')
                      this.navigateAfterFloor('/setup/setupwizard/step1');
                    } else {
                      // All bots are ready, navigate normally
                      const savedUrl = user?.state || '/bots';
                      this.router.navigateByUrl(savedUrl).catch(navError => {
                        this.router.navigateByUrl('/bots');
                      });
                    }
                  },
                  error: (err) => {
                    console.error('Error checking bot state:', err);
                    // On error, allow navigation (fail open for existing users)
                    const savedUrl = user?.state || '/bots';
                    this.router.navigateByUrl(savedUrl).catch(navError => {
                      this.router.navigateByUrl('/bots');
                    });
                  }
                });
              }
            }
          })
        )
      ),
      catchError((error: any) => {
        // On any error, redirect to onboarding (fallback)
        this.router.navigateByUrl('/setup/setupwizard/step1');
        throw error;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      error: (err) => {
        console.error('Observable error in auth-callback:', err);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
